// KPI sektör paketleri — tek kopya kaynağı (spec §5, DR6/DR7/DR12). `KPI_COPY.packages` buraya bağlanır.
// Kopya kuralı (DR12): yüzeyde HTTP kodu / "tavan" / "eşik" yok — görev dili; testle kilitli.
// e2e spec'lerindeki `COPY` blokları elle eşlenik kopyadır — birini değiştirince diğerini de güncelle.

import type { KpiPackageErrorCode } from "@mahir/shared";
import { KPI_PACKAGES_ADD_COPY } from "./kpi-packages-add-copy";
import { possessiveSuffixTr } from "./report-import-ui";

/** Sayı tr-TR binlik ayraçlı ("2.013"). */
const fmt = (n: number): string => Number(n).toLocaleString("tr-TR");
/** Sıfır olmayan parçaları " · " ile birleştirir. */
const joinParts = (parts: readonly (string | null)[]): string =>
  parts.filter((x): x is string => x !== null && x !== "").join(" · ");

const errors = {
  KPI_PACKAGE_STALE: "Paket siz düzenlerken değişti — yeniden yükleyin",
  KPI_PACKAGE_ARCHIVED:
    "Arşivdeki paket düzenlenemez ya da sete uygulanamaz — önce arşivden çıkarın",
  KPI_PACKAGE_CLIENT_REF:
    "Pakete yalnız ortak katalogdaki göstergeler girer — müşteriye özel göstergeyi önce kataloğa yükseltin",
  KPI_PACKAGE_CATEGORY_ITEM: "Kategori pakete eklenmez — kategorinin altındaki göstergeleri seçin",
  KPI_PACKAGE_ORPHAN_MEMBER: "Kırılım üyesi grubundan ayrı eklenemez — grubuyla birlikte ekleyin",
  KPI_PACKAGE_UNIT_TEMPLATE:
    "Paket birimi şablon içeremez — türetilmiş göstergenin birimi katalogdan gelir",
  KPI_PACKAGE_HAS_DEPRECATED: (n: number): string =>
    `Bu paketten set oluşturulamıyor. Kullanımdan kalkmış ${n} göstergeyi çıkarın ya da değiştirin`,
  KPI_PACKAGE_EMPTY: "Pakette gösterge yok — önce Katalogdan ekle ile gösterge seçin",
  KPI_PROMOTE_NOT_ROOT:
    "Yalnızca müşteriye özel kök gösterge yükseltilebilir — kırılım üyesini grubuyla birlikte yükseltin",
  KPI_PROMOTE_MEMBER_CONFLICT:
    "Kırılım üyesi aynı boyut/üye adıyla katalogda zaten var — önce katalogdaki üyeyi birleştirin ya da bu üyeyi kaldırın",
  KPI_PROMOTE_HAS_CLIENT_CHILDREN:
    "Bu göstergenin kırılım dışı müşteriye özel alt kalemleri var — önce onları çözün",
  KPI_PROMOTE_CHAIN_TOO_LARGE:
    "Zorunlu bağımlılık zinciri çok uzun (en çok 25) — formülleri sadeleştirin ya da parçalara bölün",
  KPI_PROMOTE_FORMULA_CLIENT_REFS:
    "Formül müşteriye özel göstergelere başvuruyor — onları da yükseltin ya da formülden çıkarın",
  KPI_SUGGEST_FAILED: "Öneri alınamadı — tekrar deneyin",
  KPI_SUGGEST_TIMEOUT: "Öneri zamanında gelmedi — tekrar deneyin",
  KPI_SUGGEST_BUSY: "Yapay zekâ şu an yoğun — biraz sonra tekrar deneyin",
  KPI_SUGGEST_LLM_CONFIG: "Yapay zekâ ayarları eksik — Ayarlar'dan yöneticinize başvurun",
  KPI_SUGGEST_CATALOG_TOO_LARGE:
    "Katalog tek seferde incelenemeyecek kadar büyük — yöneticinize başvurun",
  /** Kod çakışması — promote ve paket oluşturma ortak. */
  KPI_METRIC_CODE_TAKEN: "Bu kod katalogda kullanılıyor — başka bir kod yazın",
} satisfies Record<KpiPackageErrorCode | "KPI_METRIC_CODE_TAKEN", string | ((n: number) => string)>;

export const KPI_PACKAGES_COPY = {
  add: KPI_PACKAGES_ADD_COPY,
  page: {
    title: "Paketler",
    kicker: "PERFORMANS GÖSTERGELERİ",
    description: "Sektöre göre hazır gösterge kümeleri — set kurarken önceden işaretlenir.",
    rail: "Paket listesi",
    search: "Paket ara",
    newPackage: "+ Yeni paket",
    usedBySets: (n: number): string => `${n} sette kullanılıyor`,
    addMissingMembers: "Üyeleri ekle",
    dependencyNote: (label: string): string => `ƒ bağımlılık — ${label} için gerekli`,
    save: "Kaydet",
    cancel: "Vazgeç",
    saved: (deps: number, sets: number): string =>
      `Kaydedildi · ${deps} bağımlılık · ${sets} sete bildirim`,
    stale: "Paket siz düzenlerken değişti — yeniden yükle",
    reload: "Yeniden yükle",
    confirmSwitch: "Kaydedilmemiş değişiklikler var — paketi değiştirirseniz kaybolur.",
    confirmSwitchTitle: "Paket değiştirilsin mi?",
    confirmSwitchAction: "Değişiklikleri at",
    loadFailed: "Paketler yüklenemedi",
    loadOneFailed: "Paket yüklenemedi",
    loading: "Paket yükleniyor…",
    noSelection: "Soldan bir paket seçin",
    sectorless: "Sektörsüz",
    archiveGroup: (n: number): string => `Arşiv (${n})`,
    itemCount: (n: number): string => `${n} gösterge`,
    frameworkInfo: (name: string): string => `Çerçeve: ${name}`,
    editIdentity: "Kimliği düzenle",
    archive: "Arşivle",
    unarchive: "Arşivden çıkar",
    archived: "arşivde",
    archiveConfirmTitle: "Paket arşivlensin mi?",
    archiveConfirm: (n: number): string =>
      `Bu paket ${n} sette kullanılıyor. Arşivlenince yeni set kurulumunda ve Sete ekle'de görünmez; mevcut setler ve değerleri değişmez.`,
    archiveBlockedDraft: "Önce taslağı kaydedin ya da vazgeçin",
    savedReloadFailed: "Kaydedildi, yeniden yüklenemedi — yeniden yükle",
    catalogLoadFailed: "Katalog yüklenemedi",
    retry: "Yeniden dene",
    archivedNote: "Arşivdeki paket düzenlenemez — düzenlemek için arşivden çıkarın",
    readOnlyNote: "Paket üyelerini yalnız Veri Yönetimi yetkisi olanlar değiştirebilir",
    missingMembers: (n: number): string => `Kırılımın ${n} üyesi pakette yok`,
    treeLabel: "Paket üyeleri",
    autoReAdd: (labels: string): string => `${labels} formülü buna bakıyor; kayıtta geri eklenir`,
    draftLabel: "Kaydedilmemiş değişiklikler",
    saving: "Kaydediliyor…",
    autoAddedLine: (list: string): string => `Otomatik eklenen bağımlılıklar: ${list}`,
    autoAddedItem: (label: string, by: string): string => `${label} (${by} için gerekli)`,
    rebased: "Paket yeniden yüklendi — değişiklikleriniz yeni sürüme uygulandı",
    rebaseDropped: (list: string): string => `Artık uygulanamayan değişiklikler çıkarıldı: ${list}`,
    pickerTitle: "Katalogdan ekle",
    pickerDescription: "Seçtiğiniz göstergeler taslağa eklenir; Kaydet ile pakete yazılır.",
    pickerAdd: (n: number): string => `Taslağa ekle (${n})`,
    inPackage: "pakette zaten var",
    categoryNoEligible: "Bu kategoride eklenebilir gösterge yok",
    categoryCounter: (picked: number, eligible: number, inPackage: number): string =>
      inPackage > 0 ? `${picked} / ${eligible} · ${inPackage} pakette` : `${picked} / ${eligible}`,
  },
  header: {
    ai: "✦ AI ile öner",
    add: "+ Gösterge ekle",
    menuLabel: "Paket işlemleri",
    summary: (total: number, auto: number, sets: number, stamp: string): string =>
      `${fmt(total)} gösterge (${auto}${possessiveSuffixTr(auto)} otomatik) · ${sets} sette kullanılıyor · Son değişiklik ${stamp}`,
  },
  menu: {
    identity: "Kimliği düzenle",
    forkSet: "Bu paketten set kur",
    archive: "Arşivle",
    unarchive: "Arşivden çıkar",
  },
  filters: {
    label: "Paket içeriği süzgeci",
    search: "Pakette ara (ad ya da kod)",
    all: "Tümü",
    auto: "Otomatik eklenen",
    warning: "Uyarılı",
    changed: "Taslakta değişen",
    none: "Eşleşen gösterge yok",
    clear: "Aramayı temizle",
  },
  bands: { show: "Göster", hide: "Tümünü göster" },
  tree: {
    expandAll: "Tümünü aç",
    collapseAll: "Tümünü kapat",
    /** ağaç yükleniyor (düzenleyici iskeleti) */
    loadingTree: "Paket içeriği yükleniyor…",
  },
  detail: {
    placeholder: "Ayrıntı için bir gösterge seçin",
    closeLabel: "Ayrıntıyı kapat",
    inlineLabel: (label: string): string => `${label} — ayrıntı`,
  },
  why: {
    title: "Neden pakette?",
    manual: "Elle eklendi",
    auto: (x: string): string => `Otomatik — ${x} formülü için`,
    autoBare: "Otomatik — bir formül için",
    breakdown: (y: string): string => `Kırılım — ${y} toplamı için`,
    ai: (r: string | null): string => (r ? `AI önerisi — ${r}` : "AI önerisi"),
  },
  draft: {
    summary: (add: number, rem: number, from: number, to: number, defaults = 0): string =>
      `Taslak: ${add} ekleme · ${rem} çıkarma${defaults > 0 ? ` · ${defaults} varsayılan` : ""} · ${from} → ${to} gösterge`,
  },
  projectNew: { forkFromPackage: "Sektör paketinden set kur" },
  identity: {
    createTitle: "Yeni paket",
    editTitle: "Paket kimliği",
    name: "Ad",
    nameRequired: "Paket adı yazın",
    code: "Kod",
    codeHint: "Kod oluşturulduktan sonra değiştirilemez",
    sector: "Sektör",
    noSector: "— Sektörsüz —",
    framework: "Çerçeve",
    noFramework: "— Çerçeve yok —",
    frameworksFailed: "Çerçeve listesi alınamadı — çerçevesiz kaydedebilirsiniz",
    description: "Açıklama",
    create: "Oluştur",
    save: "Kaydet",
    cancel: "Vazgeç",
    saving: "Kaydediliyor…",
  },
  defaults: {
    note: "Değişiklik taslağa girer; Kaydet ile pakete yazılır",
    unit: "Varsayılan birim",
    unitDerived: "Türetilmiş göstergenin birimi katalogdan gelir",
    unitTooLong: "Birim en çok 32 karakter olabilir",
    formula: "Aktif formül",
    showChange: "Değişim sütunu",
    inherit: "Katalog varsayılanı",
    inheritWith: (value: string): string => `Katalog varsayılanı (${value})`,
    on: "Açık",
    off: "Kapalı",
    close: "Kapat",
  },
  tabs: {
    /** sekme listesinin erişilebilir adı */
    label: "Gösterge kaynağı",
    package: (n: number): string => `Paket (${n})`,
    /** etkin paket yokken Katalog sekmesi sayısız */
    catalogPlain: "Katalog",
    catalog: (added: number, removed: number): string => `Katalog (+${added} −${removed})`,
  },
  summary: {
    counts: (existing: number, added: number, parents: number): string =>
      `mevcut ${existing} · yeni ${added} · otomatik ${parents} üst`,
    adding: (n: number): string => `Ekleniyor… (${n})`,
    done: (n: number, parents: number): string => `${n} gösterge eklendi · ${parents} üst kategori`,
    total: (n: number): string => `${n} gösterge`,
    next: (n: number): string => `İleri (${n} gösterge)`,
    create: (n: number): string => `Oluştur (${n} gösterge)`,
    removedAdded: (removed: number, added: number): string =>
      `−${removed} çıkarıldı, +${added} eklendi`,
    autoParents: (parents: number, withMembers: number): string =>
      `${parents} üst otomatik · ${withMembers} kırılımlı üyeleriyle`,
    perPackage: (name: string, n: number): string => `${name} ${n}`,
    common: (n: number): string => `${n} ortak tek sayıldı`,
    unitPreselect: (n: number): string => `${n} göstergede paket birimi ön seçili`,
    /** türetilmiş göstergenin formül girdisi seçimde yok (bilgi, engellemez) */
    unresolved: (list: string): string =>
      `Formül girdisi seçimde yok: ${list} — bu göstergeler hesaplanamaz; girdileri de seçin`,
    unresolvedItem: (code: string, missing: string): string => `${code} (${missing})`,
    creating: "Oluşturuluyor…",
    title: "Seçim özeti",
  },
  pickerAction: {
    selectMissing: (n: number): string => `${n} eksiği seç`,
    allInSet: "tamamı sette",
    addToSet: (n: number): string => `Sete ekle (${n})`,
    boundNote: (name: string): string =>
      `Bu set ${name} paketine bağlanır; pakete eklenenler için güncelleme bandı çıkar`,
    unselect: "Seçimi kaldır",
    inSetCount: (inSet: number, total: number): string => `sette ${inSet}/${total}`,
    nothingToAdd: "Eklenecek kalem yok",
    /** paket adları tek satır — `packageNamesText` kullanır */
    namesAnd: (head: string, last: string): string => `${head} ve ${last}`,
    namesMore: (first: string, rest: number): string => `${first} +${rest} paket`,
    cancel: "Vazgeç",
    close: "Kapat",
  },
  deprecated: {
    removeFromSelection: "Seçimden çıkar",
    editPackage: "Paketi düzenle ↗",
    editPackageLabel: (name: string): string => `${name} paketini yeni sekmede düzenle`,
    recheck: "Yeniden denetle",
    blockedReason: "Kullanımdan kalkmış gösterge içeren paket seçili — önce seçimden çıkarın",
    /** DR8 satır metni — Set kur */
    forkRow: (n: number): string => `Bu paketten set kurulamaz: ${n} gösterge kullanımdan kalktı`,
    /** DR8 satır metni — Sete ekle */
    pickerRow: (n: number): string => `Bu paketten eklenemez: ${n} gösterge kullanımdan kalktı`,
  },
  empty: {
    noCategories:
      "Kategoriler Sistem Yöneticisi tarafından açılır; açılınca buradan içe aktarırsınız",
    noItemsInCatalog: "Katalog boş — gösterge eklemek için Excel'den içe aktarın",
    importFromExcel: "Excel'den kataloğa aktar",
    noPackages: "Henüz paket yok",
    firstPackage: "+ İlk paketi oluştur",
    noMembers: "Bu pakette henüz gösterge yok",
    fromCatalog: "Katalogdan seç",
    noMatch: "Eşleşen paket yok",
    noActivePackage: "Etkin paket yok — göstergeleri Katalog sekmesinden seçin",
    noSuggestion: "Öneri yok, tüm paketler",
    loadingPackages: "Paketler yükleniyor…",
  },
  errors,
  errorFallback: "Paket işlemi tamamlanamadı — yeniden deneyin",
  band: {
    kindNew: "Pakete eklenen gösterge",
    kindRemoved: "Paketten çıkarılan gösterge",
    row: (label: string, ancestors: number): string =>
      `${label} pakete eklendi · atalarıyla (${ancestors})`,
    takeToSet: "Sete al",
    infoTitle: (n: number): string => `Bilgi (${n})`,
    removedInfo: (pkg: string): string =>
      `${pkg} paketten çıkarıldı — sette kalır, değerler korunur`,
    closedSummary: (pkg: string, added: number, removed: number, formulas: number): string =>
      `${pkg}: ${added} eklendi · ${removed} çıkarıldı · Katalog: ${formulas} formül`,
    openTitle: (pkg: string, added: number, formulas: number): string =>
      `${pkg}: ${added} ekleme önerisi · Katalog: ${formulas} formül`,
    onlyInfo: "Yalnız bilgi var — yapılacak bir işlem yok",
    applyAll: (adds: number, formulas: number): string =>
      `Tümünü uygula (${adds} ekleme · ${formulas} formül)`,
    result: (added: number, withAncestors: number, formulas: number, errors: number): string =>
      `${added} eklendi (atalarıyla ${withAncestors}) · ${formulas} formül güncellendi · ${errors} hata`,
    valueChange: "Setteki → Paket değeri",
    /** ata eksiği yokken satır metni */
    rowNoAncestors: (label: string): string => `${label} pakete eklendi`,
    unnamedPackage: "Paket",
    /** kapalı özetin paket parçası (sıfır sayılar yazılmaz) */
    packagePart: (pkg: string, added: number, removed: number): string =>
      `${pkg}: ${joinParts([added > 0 ? `${added} eklendi` : null, removed > 0 ? `${removed} çıkarıldı` : null])}`,
    /** açık başlığın paket parçası (yalnız eyleme açık) */
    packageOpenPart: (pkg: string, added: number): string => `${pkg}: ${added} ekleme önerisi`,
    catalogPart: (list: string): string => `Katalog: ${list}`,
    catalogKind: {
      label: (n: number): string => `${n} etiket`,
      unit: (n: number): string => `${n} birim`,
      formula: (n: number): string => `${n} formül`,
      aggregation: (n: number): string => `${n} toplama`,
      new_metric: (n: number): string => `${n} yeni gösterge`,
    },
    addsPart: (n: number): string => `${n} ekleme`,
    applyAllParts: (list: string): string => `Tümünü uygula (${list})`,
    resultAdded: (n: number): string => `${n} eklendi`,
    /** sunucu eklenen ata sayısını verdiğinde: "3 eklendi (atalarıyla 5)" — atalar dahil toplam */
    resultAddedWithAncestors: (n: number, withAncestors: number): string =>
      `${n} eklendi (atalarıyla ${withAncestors})`,
    resultKind: {
      label: (n: number): string => `${n} etiket güncellendi`,
      unit: (n: number): string => `${n} birim güncellendi`,
      formula: (n: number): string => `${n} formül güncellendi`,
      aggregation: (n: number): string => `${n} toplama güncellendi`,
      new_metric: (n: number): string => `${n} yeni gösterge eklendi`,
    },
    resultErrors: (n: number): string => `${n} hata`,
    packageSection: "Paket önerileri",
    catalogSection: "Katalog farkları",
    applying: "Ekleniyor…",
  },
  catalog: {
    filterLabel: "Paket",
    filterPlaceholder: "Tüm göstergeler",
    filterSearch: "Paket ara",
    filterSummary: (n: number, total: number): string =>
      `Seçili paketlerden en az birinde: ${fmt(n)} / ${fmt(total)} (atalarıyla)`,
    chipRemove: (name: string): string => `${name} süzgecini kaldır`,
    noMatchInPackages: "Seçili paketlerde eşleşen gösterge yok",
    column: "Paketler",
    more: (n: number): string => `+${n}`,
    listLabel: (label: string): string => `${label} — paketleri`,
  },
  importTarget: {
    label: "Hedef paket",
    none: "— Paket yok —",
    hint: "Aktarılan göstergeler seçilen pakete de eklenir",
    newPackage: "Yeni paket…",
    newName: "Paket adı",
    newSector: "Sektör",
    create: "Oluştur",
    creating: "Oluşturuluyor…",
    cancel: "Vazgeç",
    added: (n: number): string => `${n} kalem pakete eklendi`,
    skipped: (n: number, codes: string): string => `${n} satır pakete giremedi: ${codes}`,
  },
  fromSet: {
    action: "Bu setten paket oluştur",
    title: "Bu setten paket oluştur",
    description:
      "Setteki ortak katalog göstergeleri yeni pakete üye olur; değerler ve set değişmez.",
    name: "Paket adı",
    defaultName: (set: string): string => `${set} paketi`,
    sector: "Sektör",
    create: "Paketi oluştur",
    creating: "Oluşturuluyor…",
    cancel: "Vazgeç",
    close: "Kapat",
    done: (name: string, n: number): string => `Paket oluşturuldu: ${name} (${n} üye)`,
    open: "Paketi aç",
    skippedTitle: (n: number): string => `${n} gösterge atlandı — önce kataloğa yükseltin`,
    /** KPI_PACKAGE_EMPTY — setten paket oluşturmada genel paket metni yerine */
    emptyError:
      "Sette pakete alınabilecek gösterge yok — müşteri-özel göstergeleri önce kataloğa yükseltin",
  },
  promote: {
    action: "Kataloğa yükselt…",
    title: "Kataloğa yükselt",
    labelField: "Katalog adı",
    codeField: "Yeni kod",
    parentField: "Üst kategori",
    checking: "Denetleniyor…",
    customerName: "Müşteri adı ortak kataloğa girecek",
    chainTitle: (n: number): string => `Zorunlu bağımlılıklar · ${n} (en çok 25)`,
    chainHead: {
      indicator: "Gösterge",
      label: "Katalog adı",
      code: "Yeni kod",
      parent: "Üst kategori",
    },
    changeParent: "değiştir",
    memberSummary: (n: number): string => `+${n} kırılım üyesi (kodlar aynı kuralla)`,
    rewrite: (setFormulas: number, catalogFormulas: number, unitTemplates: number): string =>
      `Yeniden yazılacak: ${setFormulas} set formülü · ${catalogFormulas} katalog formülü · ${unitTemplates} birim şablonu`,
    addToPackages: "Paketlere ekle (bağımlılıklar da girer)",
    blockers: (n: number): string => `✕ ${n} engel`,
    goToRow: "satıra git",
    parentProblem: {
      missing:
        "Üst kategori seçin — ortak katalogda kategori yoksa Sistem Yöneticisi bir kategori açmalı",
      not_found: "Seçilen üst kategori bulunamadı — listeden yeniden seçin",
      not_category: "Seçilen üst bir kategori değil — kategori seçin",
      not_global: "Üst kategori müşteriye özel — ortak bir kategori seçin",
    },
    chainTooLarge: "Zorunlu bağımlılık zinciri çok uzun (en çok 25) — formülleri sadeleştirin",
    memberConflict:
      "Kırılım üyesi aynı boyut/üye adıyla katalogda zaten var — önce katalogdaki üyeyi birleştirin ya da bu üyeyi kaldırın",
    clientChildren:
      "Bu göstergenin kırılım dışı müşteriye özel alt kalemleri var — önce onları çözün",
    confirmTitle: "Kataloğa yükseltilsin mi?",
    confirm: (n: number): string =>
      `Geri alınamaz: ${n} gösterge tüm müşterilerin kataloğunda görünür. Değerler paylaşılmaz, başka setlere otomatik eklenmez.`,
    confirmAction: "Yükselt",
    cancel: "Vazgeç",
    done: (n: number): string => `${n} gösterge kataloğa yükseltildi`,
    needPromoteFirst: "önce kataloğa yükseltin",
    noChain: "Bağımlılık yok",
    /** IndicatorModal üst satırı — göstergenin kapsamı (Promote durum matrisi) */
    scopeClient: "müşteriye özel",
    scopeCatalog: "katalog",
    /** Sayfada kaydedilmemiş set değişikliği varken yükseltme kilitli (yeniden yükleme taslağı siler) */
    saveFirst: "Önce veri setindeki değişiklikleri kaydedin",
    parentRequired: "Üst kategori seçin",
    parentEmpty: "— Seçin —",
    noCategories:
      "Ortak katalogda kategori yok — Sistem Yöneticisi bir kategori açmalı (ör. Sektörel)",
    forbidden:
      "Bu yükseltme bir kategoriyi de ortak kataloğa taşır — yalnız Sistem Yöneticisi yapabilir",
    clientRefs:
      "Formül başka müşteriye özel ya da kategori göstergelerine başvuruyor — önce şunları çözün",
    codesList: (codes: string): string => `Kodlar: ${codes}`,
    codeTaken: "Kod kullanımda — başka bir kod yazın",
    packagesEmpty: "Etkin paket yok",
    previewTitle: "Önizleme",
    promoting: "Yükseltiliyor…",
    rowLabel: (label: string): string => `${label} — katalog adı`,
    rowCode: (label: string): string => `${label} — yeni kod`,
    rowParent: (label: string): string => `${label} — üst kategori`,
    rowStatus: "Durum",
  },
  hint: {
    clientSector: (pkg: string, n: number): string =>
      `Bu sektör için hazır paket var: ${pkg} (${n}) — Set kur`,
    /** ipucu satırının metin parçası; "Set kur" ayrı düğme */
    clientSectorText: (pkg: string, n: number): string =>
      `Bu sektör için hazır paket var: ${pkg} (${n})`,
    setUp: "Set kur",
    dismissLabel: "Paket ipucunu kapat",
    source: (pkg: string): string => `Kaynak: ${pkg} paketi`,
    setBound: (pkg: string): string =>
      `Bu set ${pkg} paketine bağlanır; pakete eklenenler için güncelleme bandı çıkar`,
    dismiss: "Kapat",
  },
  suggest: {
    costLabel: "Paket önerisi",
  },
  pills: {
    suggested: "önerilen",
    deprecated: "kullanımdan kalktı",
    partialMember: "kısmi üye",
    conflict: "çakışma",
    allInSet: "tamamı sette",
    package: "paket",
    packageDefault: "paket varsayılanı",
    autoFor: (x: string): string => `otomatik · ${x} için`,
    autoMore: (x: string, n: number): string => `otomatik · ${x} +${n} için`,
    autoBare: "otomatik",
    removing: "çıkarılacak",
    adding: "eklenecek",
    defaultUnit: (u: string): string => `varsayılan: ${u}`,
  },
} as const;

/** Paket adları tek satır: "A", "A ve B", "A, B ve C", 3'ten fazlasında "A +N paket". */
export function packageNamesText(names: readonly string[]): string {
  const P = KPI_PACKAGES_COPY.pickerAction;
  if (names.length <= 1) return names[0] ?? "";
  if (names.length > 3) return P.namesMore(names[0]!, names.length - 1);
  return P.namesAnd(names.slice(0, -1).join(", "), names[names.length - 1]!);
}

/** Sayaçlı metnin sayısı: doğrudan sayı ya da sunucu `details.metricIds` uzunluğu. */
function countOf(details: unknown): number | null {
  if (typeof details === "number") return details;
  const ids = (details as { metricIds?: unknown } | null | undefined)?.metricIds;
  return Array.isArray(ids) ? ids.length : null;
}

/**
 * Sunucu hata kodu → danışman metni (bilinmeyen kod → sunucu mesajı ya da genel metin). Sayaçlı
 * metin (`KPI_PACKAGE_HAS_DEPRECATED`) sayıyı `details`'ten alır; sayı yoksa "0" yazılmaz —
 * sunucu mesajına / genel metne düşer.
 */
export function packageErrorText(code: string, serverMessage?: string, details?: unknown): string {
  const t = (
    KPI_PACKAGES_COPY.errors as Record<string, string | ((n: number) => string) | undefined>
  )[code];
  if (typeof t === "function") {
    const n = countOf(details);
    return n !== null && n > 0 ? t(n) : (serverMessage ?? KPI_PACKAGES_COPY.errorFallback);
  }
  return t ?? serverMessage ?? KPI_PACKAGES_COPY.errorFallback;
}

/** Hata bu kopya tablosunda mı (paket / promote kodları) — çağıran diğer kopyalara düşer. */
export function isPackageErrorCode(code: string): boolean {
  return Object.prototype.hasOwnProperty.call(KPI_PACKAGES_COPY.errors, code);
}
